/**
 * Per-crew feature-module registry
 *
 * Single source of truth for which of the toggleable modules a crew's plan
 * (tier + crewType) even makes available, which modules depend on which
 * others, and how those two facts combine with a crew's manual per-module
 * on/off flag into one resolved "is this usable right now" answer.
 */

import { CrewTier, CrewType } from './crew.entities';

export type CrewModuleId =
  | 'mentorship'
  | 'mentorshipPlans'
  | 'goals'
  | 'badges'
  | 'collabBoards'
  | 'retrospectives'
  | 'feeds'
  | 'pulseCheck'
  | 'quickPoll'
  | 'projects'
  | 'competencyDefinitions'
  | 'bulkMemberManagement'
  | 'orgChart'
  | 'performanceReviews';

export interface CrewModuleDefinition {
  id: CrewModuleId;
  label: string;
  description: string;
  /** Tiers whose plan includes this module at all (independent of the manual admin toggle). */
  tiers: CrewTier[];
  /** If set, only these crewTypes can ever see/use this module, regardless of tier. */
  crewTypes?: CrewType[];
  /** Other module ids that must ALSO be enabled for this module to function. */
  dependsOn?: CrewModuleId[];
  /** Default state for new crews when the module is available. */
  defaultEnabled: boolean;
}

export const CREW_MODULE_REGISTRY: Record<CrewModuleId, CrewModuleDefinition> = {
  mentorship: {
    id: 'mentorship',
    label: 'Mentorship',
    description: 'Self-serve mentor/mentee relationships, requests, and reviews.',
    tiers: ['free', 'pro', 'enterprise'],
    defaultEnabled: true
  },
  mentorshipPlans: {
    id: 'mentorshipPlans',
    label: 'Mentorship Plans',
    description: 'Admin-managed mentorship periods, matching, and pairing plans.',
    tiers: ['pro', 'enterprise'],
    // Corporate-only: the /mentorship/periods routes are gated by
    // corporateCrewOnlyMiddleware, so the registry must agree or clients
    // fire period fetches that the server 403s ("not available for Hobby Crews").
    crewTypes: ['corporate'],
    // Independent of self-serve mentorship — a crew can run admin-managed
    // mentorship without enabling self-directed mentorship, and vice versa.
    defaultEnabled: true
  },
  goals: {
    id: 'goals',
    label: 'Goals',
    description: 'Crew, team, profile, and mentorship goal tracking with key results.',
    tiers: ['free', 'pro', 'enterprise'],
    defaultEnabled: true
  },
  badges: {
    id: 'badges',
    label: 'Badges',
    description: 'Crew badges for achievements and recognition.',
    tiers: ['free', 'pro', 'enterprise'],
    defaultEnabled: true
  },
  collabBoards: {
    id: 'collabBoards',
    label: 'Collab Boards',
    description: 'Collaborative sticky-note boards for brainstorms and workshops.',
    tiers: ['pro', 'enterprise'],
    defaultEnabled: true
  },
  retrospectives: {
    id: 'retrospectives',
    label: 'Retrospectives',
    description: 'Team retro boards, action items, and timers.',
    tiers: ['pro', 'enterprise'],
    defaultEnabled: true
  },
  feeds: {
    id: 'feeds',
    label: 'Feeds',
    description: 'Team and crew activity feeds with posts, comments, and reactions.',
    tiers: ['pro', 'enterprise'],
    defaultEnabled: true
  },
  pulseCheck: {
    id: 'pulseCheck',
    label: 'Pulse Check',
    description: 'Structured peer-feedback surveys with generated insights.',
    tiers: ['pro', 'enterprise'],
    defaultEnabled: true
  },
  quickPoll: {
    id: 'quickPoll',
    label: 'Quick Poll',
    description: 'Lightweight one-off polls for the crew or specific members.',
    tiers: ['pro', 'enterprise'],
    defaultEnabled: true
  },
  projects: {
    id: 'projects',
    label: 'Projects',
    description: 'Individual and team project showcases with galleries and comments.',
    tiers: ['free', 'pro', 'enterprise'],
    defaultEnabled: true
  },
  competencyDefinitions: {
    id: 'competencyDefinitions',
    label: 'Competency Definitions',
    description: 'Define and track competencies for members within the crew.',
    tiers: ['pro', 'enterprise'],
    crewTypes: ['corporate'],
    defaultEnabled: true
  },
  bulkMemberManagement: {
    id: 'bulkMemberManagement',
    label: 'Bulk Member Management',
    description: 'Bulk-create and bulk-edit managed profiles.',
    tiers: ['pro', 'enterprise'],
    crewTypes: ['corporate'],
    defaultEnabled: true
  },
  orgChart: {
    id: 'orgChart',
    label: 'Org Chart',
    description: 'Manager relationships, org chart, and reporting-line views.',
    tiers: ['free', 'pro', 'enterprise'],
    crewTypes: ['corporate'],
    defaultEnabled: true
  },
  performanceReviews: {
    id: 'performanceReviews',
    label: 'Performance Reviews',
    description: 'Review cycles with self, manager, peer, skip-level, and direct-report reviews.',
    tiers: ['pro', 'enterprise'],
    crewTypes: ['corporate'],
    defaultEnabled: true
  }
};

/** Maps a module id to its Crew.featureFlags boolean key. */
export const MODULE_FLAG_KEY: Record<CrewModuleId, string> = {
  mentorship: 'mentorshipEnabled',
  mentorshipPlans: 'mentorshipPlansEnabled',
  goals: 'goalsEnabled',
  badges: 'badgesEnabled',
  collabBoards: 'boardsEnabled',
  retrospectives: 'retroEnabled',
  feeds: 'feedsEnabled',
  pulseCheck: 'pulseCheckEnabled',
  quickPoll: 'quickPollEnabled',
  projects: 'projectsEnabled',
  competencyDefinitions: 'competencyDefinitionsEnabled',
  bulkMemberManagement: 'bulkMemberManagementEnabled',
  orgChart: 'orgChartEnabled',
  performanceReviews: 'performanceReviewsEnabled'
};

interface ModuleGateCrew {
  tier: CrewTier;
  crewType: CrewType;
  /** Crew-admin preference layer: `<moduleId>Enabled` booleans; absent = on. */
  featureFlags?: Partial<Record<string, boolean>>;
  /** Persera (platform) override layer: moduleId → true grants access beyond tier. */
  moduleOverrides?: Partial<Record<string, boolean>>;
}

/**
 * Registry-only check: is this module part of the crew's TIER/crewType plan,
 * ignoring both the Persera override and the crew-admin preference. Use for
 * pricing/marketing ("what the plan includes"). Fails open when crew is absent.
 */
/**
 * A crew's tier for the purpose of feature gating. Hobby crews are always on the
 * free plan by policy, so we treat them as `free` here regardless of the `tier`
 * value actually stored — a stored `tier` can drift out of sync (e.g. a paid
 * crew converted to hobby, where the `tier` field is immutable and can't be
 * coerced back to `free`). Gating off the effective tier makes that drift
 * harmless instead of leaking paid modules to hobby crews.
 */
export function getEffectiveTier(crew: ModuleGateCrew): CrewTier {
  return crew.crewType === 'hobby' ? 'free' : crew.tier;
}

export function isModuleAvailable(moduleId: CrewModuleId, crew: ModuleGateCrew | null | undefined): boolean {
  if (!crew) return true;
  const def = CREW_MODULE_REGISTRY[moduleId];
  if (!def.tiers.includes(getEffectiveTier(crew))) return false;
  if (def.crewTypes && !def.crewTypes.includes(crew.crewType)) return false;
  return true;
}

/**
 * Availability layer: is this module available to the crew — i.e. the tier/crewType
 * plan includes it OR a Persera admin has granted it for this crew (grant-only:
 * an override of `true` adds access beyond tier; it never removes tier access).
 * This is what decides whether a crew admin even sees a preference toggle.
 */
export function isModuleAvailableForCrew(moduleId: CrewModuleId, crew: ModuleGateCrew | null | undefined): boolean {
  if (crew?.moduleOverrides?.[moduleId] === true) return true;
  return isModuleAvailable(moduleId, crew);
}

export function getModuleDependencies(moduleId: CrewModuleId): CrewModuleId[] {
  return CREW_MODULE_REGISTRY[moduleId].dependsOn ?? [];
}

/** All modules that (transitively) depend on moduleId — used for cascade-off UI warnings. */
export function getDependentModules(moduleId: CrewModuleId): CrewModuleId[] {
  const dependents = new Set<CrewModuleId>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of Object.keys(CREW_MODULE_REGISTRY) as CrewModuleId[]) {
      if (dependents.has(id) || id === moduleId) continue;
      const deps = getModuleDependencies(id);
      if (deps.includes(moduleId) || deps.some(dep => dependents.has(dep))) {
        dependents.add(id);
        changed = true;
      }
    }
  }
  return Array.from(dependents);
}

/**
 * The single combined source of truth. A module is ENABLED when:
 *   1. it's AVAILABLE — tier/crewType plan includes it OR Persera granted it
 *      (isModuleAvailableForCrew), AND
 *   2. the crew admin hasn't turned it off — featureFlags preference, default on, AND
 *   3. its dependencies are also enabled.
 * Availability is checked BEFORE the preference flag, so a preference value for a
 * module the crew can't access is simply moot — no backfill needed for pre-existing
 * crews, and a crew's "off" preference persists harmlessly while unavailable.
 */
export function isModuleEnabledForCrew(moduleId: CrewModuleId, crew: ModuleGateCrew | null | undefined): boolean {
  if (!isModuleAvailableForCrew(moduleId, crew)) return false;
  const flagKey = MODULE_FLAG_KEY[moduleId];
  if (crew?.featureFlags?.[flagKey] === false) return false;
  return getModuleDependencies(moduleId).every(dep => isModuleEnabledForCrew(dep, crew));
}
